import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import classNames from 'clsx';

import CommonDialog from '../../shared/dialog/CommonDialog';
import PillButton from '../../shared/button/PillButton';
import assetUrl from '../../../utils/assetUrl';
import {
  closeUserRegistrationDialog,
  openUserRegistrationDialog,
  requestUserRegistrationDialogOpen,
} from '../../../actions/dialog';
import isContentPage from '../../../utils/subscription/isContentPage';
import { logWebActivity } from '../../../actions/logging';

import registrationViews from '../../../constants/dialogs/registrationViews';
import web from '../../../constants/analytics/categoryActionLabel/web';
import {
  USER_REGISTRATION_DIALOG_BUTTON,
  USER_REGISTRATION_DIALOG_HEADER,
  USER_REGISTRATION_DIALOG_SUB_HEADER,
} from '../../../constants/localizations/userRegistration';
import breakpoints from '../../../styles/breakpoints';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import { LOGIN_PATH } from '../../../constants/paths';
import GoPremiumHint from './GoPremiumHint';
import LinkWithAlexaHint from './LinkWithAlexaHint';

import css from './userRegistrationDialog.module.scss';

const mobileLayout = {
  bodyClassName: '',
  contentClassName: css.mobileDialogContent,
};

const desktopLayout = {
  bodyClassName: css.desktopDialogBody,
  contentClassName: css.desktopDialogContent,
};

export class UserRegistrationDialog extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      openDialog: PropTypes.func.isRequired,
      closeDialog: PropTypes.func.isRequired,
      requestDialogOpen: PropTypes.func.isRequired,
      logWebActivity: PropTypes.func.isRequired,
    }),
    breakpoint: PropTypes.number.isRequired,
    dialogIsOpen: PropTypes.bool.isRequired,
    dialogOpenIsRequested: PropTypes.bool.isRequired,
    pendingOpenRequest: PropTypes.bool.isRequired,
    routeProps: PropTypes.object.isRequired,
    dialogOpenRequest: PropTypes.func.isRequired,
    handleDialogClose: PropTypes.func.isRequired,
    isSubscriptionEnabled: PropTypes.bool.isRequired,
    dialogView: PropTypes.string,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);
    this.logAndClose = this.logAndClose.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
  }

  componentDidMount() {
    const { actions, dialogOpenRequest, pendingOpenRequest } = this.props;
    const { requestDialogOpen, closeDialog } = actions;

    if (pendingOpenRequest) {
      dialogOpenRequest(requestDialogOpen, closeDialog, true);
    }
  }

  componentDidUpdate(prevProps) {
    const { actions, routeProps, dialogOpenIsRequested } = this.props;

    if (
      !prevProps.dialogOpenIsRequested &&
      dialogOpenIsRequested &&
      (routeProps.matchUrl === LOGIN_PATH ||
        routeProps.isLanding ||
        isContentPage(routeProps))
    ) {
      actions.openDialog();
      actions.logWebActivity(web.actions.show, web.labels.registrationDialog);
    }
  }

  getLayoutClasses() {
    return this.props.breakpoint <= breakpoints.BREAKPOINT_360
      ? mobileLayout
      : desktopLayout;
  }

  logAndClose() {
    this.closeDialog();
    this.props.actions.logWebActivity(
      web.actions.close,
      web.labels.registrationDialog,
    );
  }

  closeDialog() {
    const { actions, handleDialogClose } = this.props;
    actions.closeDialog();
    handleDialogClose();
  }

  render() {
    const { dialogIsOpen, dialogView, isSubscriptionEnabled } = this.props;
    const { getLocalizedText } = this.context;
    const { bodyClassName, contentClassName } = this.getLayoutClasses();

    let dialogHint = null;

    if (
      dialogIsOpen &&
      dialogView === registrationViews.goPremium &&
      isSubscriptionEnabled
    ) {
      dialogHint = (
        <GoPremiumHint
          onGoPremiumClick={this.closeDialog}
          className={css.hint}
        />
      );
    }

    if (dialogIsOpen && dialogView === registrationViews.linkWithAlexa) {
      dialogHint = (
        <LinkWithAlexaHint
          onLinkWithAlexaClick={this.closeDialog}
          className={css.hint}
        />
      );
    }

    return (
      <CommonDialog
        dialogOpen={dialogIsOpen}
        handleDialogClose={this.logAndClose}
        bodyClassName={classNames(css.dialogBody, bodyClassName)}
        contentClassName={classNames(css.dialogContent, contentClassName)}
        disableOverlayClickToClose
      >
        <img
          id="regImageOne"
          data-testid="regImageOne"
          alt="device illustration"
          className={css.image}
          src={assetUrl('assets/img/device_illustration.svg')}
        />
        <div className={css.content}>
          <div className={css.headerContainer}>
            <h1
              id="regDialogHeader"
              data-testid="regDialogHeader"
              className={css.heading}
            >
              {getLocalizedText(USER_REGISTRATION_DIALOG_HEADER)}
            </h1>
            <p
              id="regDialogSubHeader"
              data-testid="regDialogSubHeader"
              className={css.subHeading}
            >
              {getLocalizedText(USER_REGISTRATION_DIALOG_SUB_HEADER)}
            </p>
          </div>
          {dialogHint}
          <PillButton
            id="userRegistrationDialog"
            onClick={this.logAndClose}
            className={css.button}
            label={getLocalizedText(USER_REGISTRATION_DIALOG_BUTTON)}
          />
        </div>
      </CommonDialog>
    );
  }
}

function mapStateToProps(state) {
  const {
    registrationDialogView,
    registrationDialogIsOpen,
    registrationDialogOpenRequested,
    pendingRegistrationDialogOpenRequest,
  } = state.dialog;
  return {
    dialogView: registrationDialogView,
    dialogIsOpen: registrationDialogIsOpen,
    dialogOpenIsRequested: registrationDialogOpenRequested,
    pendingOpenRequest: pendingRegistrationDialogOpenRequest,
    breakpoint: state.app.breakpoint,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        openDialog: openUserRegistrationDialog,
        closeDialog: closeUserRegistrationDialog,
        requestDialogOpen: requestUserRegistrationDialogOpen,
        logWebActivity,
      },
      dispatch,
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UserRegistrationDialog);
